import React, { useState, useRef } from 'react';
import { CreditCard, ArrowRight, Loader2, CheckCircle, Briefcase, Lock, BookOpen, Target, Eye, EyeOff, Plus, Trash2, TrendingUp, Star, Upload, User, FileText, X, Camera, ExternalLink, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { AddressAutocomplete } from '@/components/broker-registration/AddressAutocomplete';
import { BUYER_TRANSLATIONS } from '@/constants/buyer-registration.translations';
import { invokeFunction } from '@/lib/cloudrun';
import { startSubscriptionCheckout, startOneTimeCheckout } from '@/lib/api';
import { saveRegistrationData } from '@/services/registration-data.service'
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CANADIAN_PROVINCES, US_STATES, EUROPEAN_COUNTRIES, OTHER_COUNTRIES } from '@/constants/lenderRegistration';
import {
  addRoleToCurrentUser,
  assignRoleViaNestJS,
  getErrorMessage,
  isEmailAlreadyInUseError,
  sendVerificationEmailBestEffort,
} from '@/lib/registration-roles';

// --- CONSTANTES GLOBALES ---
const TIERS = {
  essential: {
    priceId: 'price_1TcybjCkUJGKtqQA6SoIwGB9',
    price: 999,
    regularPrice: 1499,
    name: { en: 'Essential', fr: 'Essentiel' },
  },
  essentialPlus: {
    priceId: 'price_1TcycuCkUJGKtqQAX7BA3dSX',
    price: 2199,
    regularPrice: 3299,
    name: { en: 'Essential Plus', fr: 'Essentiel Plus' },
  },
  acquisitionPro: {
    priceId: 'price_1TcyhOCkUJGKtqQAuWnc1P59',
    price: 3999,
    regularPrice: 5999,
    name: {
      en: 'Acquisition Pro',
      fr: 'Acquisition Pro'
    },
  },
  byod: {
    priceId: 'price_1Td0YPCkUJGKtqQAf0h1sMue',
    price: 499,
    regularPrice: 720,
    name: { en: 'BYOD', fr: 'BYOD' },
  },
};

const ANNEES_EXPERIENCE = Array.from({ length: 50 }, (_, i) => i + 1);

// --- Types ---
interface SectorDocument {
  file: File;
  type: 'cv' | 'competency' | 'award';
  name: string;
}

interface BuyerData {
  firstName: string;
  lastName: string;
  email: string;
  emailConfirmation: string;
  motDePasse: string;
  confirmMotDePasse: string;
  telephone: string;
  adresseRue: string;
  ville: string;
  province: string;
  codePostal: string;
  pays: string;
  sourcesMarketing: string[];
  secteurs: Secteur[];
  profilePhoto: File | null;
  selfDescription: string;
  experience: string;
}

interface Secteur {
  secteur: string;
  anneesExperience: string;
  licencePermis: string;
  juridictionsCanada: string[];
  juridictionsUSA: string[];
  juridictionsEurope: string[];
  juridictionsOther: string[];
  documents: SectorDocument[];
}

interface FormErrors {
  [key: string]: string;
}

type TranslationType = typeof BUYER_TRANSLATIONS.en;

// --- ÉTAPE 1: Informations Personnelles ---
const PersonalInfoForm = ({ 
  initialData, 
  nextStep,
  t
}: { 
  initialData: BuyerData; 
  nextStep: (data: Partial<BuyerData>) => void;
  t: TranslationType;
}) => {
  const [formData, setFormData] = useState({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    email: initialData.email || '',
    emailConfirmation: initialData.emailConfirmation || '',
    motDePasse: initialData.motDePasse || '',
    confirmMotDePasse: initialData.confirmMotDePasse || '',
    telephone: initialData.telephone || '',
    adresseRue: initialData.adresseRue || '',
    ville: initialData.ville || '',
    province: initialData.province || '',
    codePostal: initialData.codePostal || '',
    pays: initialData.pays || '',
    sourcesMarketing: initialData.sourcesMarketing || []
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (touched && errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSourceToggle = (source: string) => {
    const currentSources = formData.sourcesMarketing || [];
    const newSources = currentSources.includes(source)
      ? currentSources.filter(s => s !== source)
      : [...currentSources, source];
    
    setFormData(prev => ({ ...prev, sourcesMarketing: newSources }));
    
    if (touched && errors.sourcesMarketing && newSources.length > 0) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sourcesMarketing;
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: FormErrors = {};
    
    if (!formData.firstName?.trim()) {
      newErrors.firstName = t.errors.firstNameRequired;
    }
    
    if (!formData.lastName?.trim()) {
      newErrors.lastName = t.errors.lastNameRequired;
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = t.errors.emailRequired;
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = t.errors.emailInvalid;
    }
    
    if (!formData.emailConfirmation?.trim()) {
      newErrors.emailConfirmation = t.errors.confirmEmailRequired;
    } else if (formData.emailConfirmation !== formData.email) {
      newErrors.emailConfirmation = t.errors.emailMismatch;
    }
    
    if (!formData.motDePasse) {
      newErrors.motDePasse = t.errors.passwordRequired;
    } else if (formData.motDePasse.length < 6) {
      newErrors.motDePasse = t.errors.passwordMinLength;
    }
    
    if (!formData.confirmMotDePasse) {
      newErrors.confirmMotDePasse = t.errors.confirmPasswordRequired;
    } else if (formData.confirmMotDePasse !== formData.motDePasse) {
      newErrors.confirmMotDePasse = t.errors.passwordMismatch;
    }
    
    if (!formData.telephone?.trim()) {
      newErrors.telephone = t.errors.phoneRequired;
    }
    
    if (!formData.adresseRue?.trim()) {
      newErrors.adresseRue = t.errors.addressRequired;
    }
    
    if (!formData.ville?.trim()) {
      newErrors.ville = t.errors.cityRequired;
    }
    
    if (!formData.province?.trim()) {
      newErrors.province = t.errors.provinceRequired;
    }
    
    if (!formData.codePostal?.trim()) {
      newErrors.codePostal = t.errors.postalCodeRequired;
    }
    
    if (!formData.pays?.trim()) {
      newErrors.pays = t.errors.countryRequired;
    }
    
    if (!formData.sourcesMarketing || formData.sourcesMarketing.length === 0) {
      newErrors.sourcesMarketing = t.errors.sourcesRequired;
    }
    
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    
    const validationErrors = validate();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      nextStep(formData);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getProvinceOptions = () => {
    if (formData.pays === t.countries.canada) return t.provincesCanada;
    if (formData.pays === t.countries.usa) return t.statesUSA;
    return [];
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-8">
      <div className="space-y-6">
        {touched && Object.keys(errors).length > 0 && (
          <div className="p-4 bg-red-100 border-2 border-red-500 rounded-lg">
            <p className="text-red-800 font-bold text-center">
              ⚠️ {Object.keys(errors).length} {t.step1.errorsFound}
            </p>
            <p className="text-red-700 text-sm text-center mt-1">
              {t.step1.pleaseCorrect}
            </p>
          </div>
        )}

        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-sm text-red-800 font-semibold">
            ⚠️ {t.step1.requiredFields}
          </p>
        </div>

        {/* Section Profil */}
        <div className="space-y-4 p-6 bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-blue-200 rounded-xl">
          <h3 className="font-bold text-lg text-gray-800 flex items-center">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">1</div>
            {t.step1.profileSection}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.firstName}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-blue-500 transition ${
                  touched && errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.firstNamePlaceholder} 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleChange} 
              />
              {touched && errors.firstName && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.lastName}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-blue-500 transition ${
                  touched && errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.lastNamePlaceholder} 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleChange} 
              />
              {touched && errors.lastName && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.email}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-blue-500 transition ${
                  touched && errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.emailPlaceholder} 
                name="email" 
                type="email" 
                value={formData.email} 
                onChange={handleChange} 
              />
              {touched && errors.email && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.confirmEmail}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-blue-500 transition ${
                  touched && errors.emailConfirmation ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.emailPlaceholder} 
                name="emailConfirmation" 
                type="email" 
                value={formData.emailConfirmation} 
                onChange={handleChange} 
              />
              {touched && errors.emailConfirmation && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.emailConfirmation}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.password}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 pr-12 focus:ring-2 focus:ring-blue-500 transition ${
                  touched && errors.motDePasse ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="••••••••" 
                name="motDePasse" 
                type={showPassword ? 'text' : 'password'} 
                value={formData.motDePasse} 
                onChange={handleChange} 
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-11 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
              {touched && errors.motDePasse && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.motDePasse}</p>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.confirmPassword}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 pr-12 focus:ring-2 focus:ring-blue-500 transition ${
                  touched && errors.confirmMotDePasse ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="••••••••" 
                name="confirmMotDePasse" 
                type={showConfirmPassword ? 'text' : 'password'} 
                value={formData.confirmMotDePasse} 
                onChange={handleChange} 
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-11 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
              {touched && errors.confirmMotDePasse && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.confirmMotDePasse}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.phone}</label>
            <input 
              className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-blue-500 transition ${
                touched && errors.telephone ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder={t.step1.phonePlaceholder} 
              name="telephone" 
              value={formData.telephone} 
              onChange={handleChange} 
            />
            {touched && errors.telephone && (
              <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.telephone}</p>
            )}
          </div>
        </div>

        {/* Section Adresse - Fusionnée */}
        <div className="space-y-4 p-6 bg-gradient-to-br from-gray-50 to-green-50 border-2 border-green-200 rounded-xl">
          <h3 className="font-bold text-lg text-gray-800 flex items-center">
            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">2</div>
            {t.step1.addressSection}
          </h3>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.streetAddress}</label>
            <AddressAutocomplete
              value={formData.adresseRue}
              onChange={(value) => setFormData(prev => ({ ...prev, adresseRue: value }))}
              onAddressSelect={(components) => {
                const countryMapping: Record<string, string> = {
                  'CA': t.countries.canada,
                  'US': t.countries.usa,
                  'FR': 'France',
                  'BE': 'Belgium',
                  'CH': 'Switzerland',
                  'GB': 'United Kingdom',
                  'DE': 'Germany',
                  'AU': 'Australia',
                };
                setFormData(prev => ({
                  ...prev,
                  adresseRue: components.streetAddress,
                  ville: components.city,
                  province: components.stateProvince,
                  codePostal: components.zipPostalCode,
                  pays: countryMapping[components.country] || components.country,
                }));
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.adresseRue;
                  delete newErrors.ville;
                  delete newErrors.province;
                  delete newErrors.codePostal;
                  delete newErrors.pays;
                  return newErrors;
                });
              }}
              placeholder={t.step1.streetAddressPlaceholder}
              className={`!border-2 ${touched && errors.adresseRue ? '!border-red-500 !bg-red-50' : '!border-gray-300'} !bg-white !text-black`}
            />
            <p className="text-xs text-gray-500 mt-1">💡 {t.step1.addressSearchHint}</p>
            {touched && errors.adresseRue && (
              <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.adresseRue}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.city}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-green-500 transition bg-gray-100 ${
                  touched && errors.ville ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.cityPlaceholder} 
                name="ville" 
                value={formData.ville} 
                readOnly
              />
              {touched && errors.ville && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.ville}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.postalCode}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-green-500 transition bg-gray-100 ${
                  touched && errors.codePostal ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.postalCodePlaceholder} 
                name="codePostal" 
                value={formData.codePostal} 
                readOnly
              />
              {touched && errors.codePostal && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.codePostal}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.country}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-green-500 transition bg-gray-100 ${
                  touched && errors.pays ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.countryPlaceholder} 
                name="pays" 
                value={formData.pays} 
                readOnly
              />
              {touched && errors.pays && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.pays}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t.step1.province}</label>
              <input 
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-green-500 transition bg-gray-100 ${
                  touched && errors.province ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={t.step1.provincePlaceholder} 
                name="province" 
                value={formData.province} 
                readOnly
              />
              {touched && errors.province && (
                <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors.province}</p>
              )}
            </div>
          </div>
        </div>

        {/* Section Source Marketing */}
        <div className="space-y-4 p-6 bg-gradient-to-br from-gray-50 to-indigo-50 border-2 border-indigo-200 rounded-xl">
          <h3 className="font-bold text-lg text-gray-800 flex items-center">
            <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">3</div>
            {t.step1.sourcesTitle}
          </h3>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              {t.step1.sourcesHint} *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(t.sources).map(([key, label]) => (
                <label 
                  key={key} 
                  className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.sourcesMarketing.includes(label) 
                      ? 'border-indigo-600 bg-indigo-50' 
                      : 'border-gray-300 hover:border-indigo-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.sourcesMarketing.includes(label)}
                    onChange={() => handleSourceToggle(label)}
                    className="form-checkbox h-5 w-5 text-indigo-600 rounded mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                </label>
              ))}
            </div>
            {touched && errors.sourcesMarketing && (
              <p className="text-sm text-red-600 mt-2 font-medium">⚠️ {errors.sourcesMarketing}</p>
            )}
            {formData.sourcesMarketing.length > 0 && (
              <p className="text-xs text-green-600 mt-2 font-medium">
                ✓ {formData.sourcesMarketing.length} {t.step1.sourcesSelected}
              </p>
            )}
          </div>
        </div>

        <button 
          type="submit" 
          className="w-full rounded-lg bg-[#CEAB65] hover:bg-[#b8954f] px-8 py-4 text-[#1D314D] text-lg font-bold transition-all duration-300 shadow-lg flex items-center justify-center"
        >
          {t.step1.continueButton}
          <ArrowRight className="ml-3 h-6 w-6" />
        </button>
      </div>
    </form>
  );
};

// --- ÉTAPE 2: Secteurs d'Activité avec Documents ---
const SectorExpertiseForm = ({ 
  initialData, 
  nextStep, 
  prevStep,
  t
}: { 
  initialData: BuyerData; 
  nextStep: (data: Partial<BuyerData>) => void;
  prevStep: () => void;
  t: TranslationType;
}) => {
  const [formData] = useState(initialData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [secteurs, setSecteurs] = useState<Secteur[]>(initialData.secteurs.length > 0 ? initialData.secteurs : [{
    secteur: '', 
    anneesExperience: '', 
    licencePermis: '',
    juridictionsCanada: [],
    juridictionsUSA: [],
    juridictionsEurope: [],
    juridictionsOther: [],
    documents: []
  }]);

  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const { language } = useLanguage();
  const lang: 'en' | 'fr' = language === 'fr' ? 'fr' : 'en';

  const addSecteur = () => {
    if (secteurs.length < 5) {
      setSecteurs([...secteurs, { 
        secteur: '', 
        anneesExperience: '', 
        licencePermis: '',
        juridictionsCanada: [],
        juridictionsUSA: [],
        juridictionsEurope: [],
        juridictionsOther: [],
        documents: []
      }]);
    }
  };

  const removeSecteur = (index: number) => {
    if (secteurs.length > 1) {
      setSecteurs(secteurs.filter((_, i) => i !== index));
    }
  };

  const handleSecteurChange = (index: number, field: 'secteur' | 'anneesExperience' | 'licencePermis', value: string) => {
    const newSecteurs = [...secteurs];
    newSecteurs[index][field] = value;
    setSecteurs(newSecteurs);
  };


  const handleDocumentUpload = (sectorIndex: number, docType: 'cv' | 'competency' | 'award', e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newSecteurs = [...secteurs];
    const newDocs: SectorDocument[] = Array.from(files).map(file => ({
      file,
      type: docType,
      name: file.name
    }));
    
    newSecteurs[sectorIndex].documents = [...newSecteurs[sectorIndex].documents, ...newDocs];
    setSecteurs(newSecteurs);
    
    // Reset file input
    if (e.target) e.target.value = '';
  };

  const removeDocument = (sectorIndex: number, docIndex: number) => {
    const newSecteurs = [...secteurs];
    newSecteurs[sectorIndex].documents = newSecteurs[sectorIndex].documents.filter((_, i) => i !== docIndex);
    setSecteurs(newSecteurs);
  };

  const getDocTypeLabel = (type: 'cv' | 'competency' | 'award') => {
    const labels = {
      cv: t.step2.docTypeCV || 'CV',
      competency: t.step2.docTypeCompetency || 'Competency Card',
      award: t.step2.docTypeAward || 'Award'
    };
    return labels[type];
  };

  const validate = () => {
    const newErrors: FormErrors = {};
    
    secteurs.forEach((secteur, index) => {
      if (!secteur.secteur) {
        newErrors[`secteur_${index}`] = t.errors.sectorRequired;
      }
      if (!secteur.anneesExperience) {
        newErrors[`experience_${index}`] = t.errors.experienceRequired;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      nextStep({ ...formData, secteurs });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderError = (field: string) => errors[field] ? <p className="text-sm text-red-600 mt-1 font-medium">⚠️ {errors[field]}</p> : null;

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-8">
      <div className="space-y-6">
        <div className="bg-[#CEAB65]/10 border-l-4 border-[#CEAB65] p-4 rounded">
          <p className="text-sm text-[#1D314D] font-semibold">
            💼 {t.step2.hint}
          </p>
        </div>

        {secteurs.map((secteur, index) => (
          <div key={index} className="space-y-4 p-6 bg-gradient-to-br from-gray-50 to-purple-50 border-2 border-purple-200 rounded-xl relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-gray-800 flex items-center">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
                  {index + 1}
                </div>
                {t.step2.sector} {index + 1}
              </h3>
              {secteurs.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSecteur(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.step2.sectorLabel}
              </label>
              <select
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-purple-500 transition ${errors[`secteur_${index}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                value={secteur.secteur}
                onChange={(e) => handleSecteurChange(index, 'secteur', e.target.value)}
              >
                <option value="">{t.step2.sectorPlaceholder}</option>
                {t.sectors.map((sect) => (
                  <option key={sect} value={sect}>{sect}</option>
                ))}
              </select>
              {renderError(`secteur_${index}`)}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.step2.experienceLabel}
              </label>
              <select
                className={`w-full rounded-lg border-2 p-3 focus:ring-2 focus:ring-purple-500 transition ${errors[`experience_${index}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                value={secteur.anneesExperience}
                onChange={(e) => handleSecteurChange(index, 'anneesExperience', e.target.value)}
              >
                <option value="">{t.step2.experiencePlaceholder}</option>
                {ANNEES_EXPERIENCE.map((annee) => (
                  <option key={annee} value={annee}>
                    {annee} {annee === 1 ? t.step2.year : t.step2.years}
                  </option>
                ))}
              </select>
              {renderError(`experience_${index}`)}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.step2.licensesLabel}
              </label>
              <input
                className="w-full rounded-lg border-2 border-gray-300 p-3 focus:ring-2 focus:ring-purple-500 transition"
                placeholder={t.step2.licensesPlaceholder}
                value={secteur.licencePermis}
                onChange={(e) => handleSecteurChange(index, 'licencePermis', e.target.value)}
              />
            </div>

            {/* Documents Upload Section */}
            <div className="space-y-3 p-4 bg-white/50 rounded-lg border border-purple-100">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📁 {t.step2.documentsLabel || 'Supporting Documents (optional)'}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {t.step2.documentsHint || 'Upload CV, competency cards, awards, or other relevant documents for this sector'}
              </p>
              
              <div className="flex flex-wrap gap-2">
                <input
                  type="file"
                  ref={(el) => { fileInputRefs.current[`cv_${index}`] = el; }}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => handleDocumentUpload(index, 'cv', e)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[`cv_${index}`]?.click()}
                  className="flex items-center px-3 py-2 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  {t.step2.uploadCV || 'CV / Resume'}
                </button>

                <input
                  type="file"
                  ref={(el) => { fileInputRefs.current[`competency_${index}`] = el; }}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => handleDocumentUpload(index, 'competency', e)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[`competency_${index}`]?.click()}
                  className="flex items-center px-3 py-2 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition"
                >
                  <Briefcase className="w-4 h-4 mr-1" />
                  {t.step2.uploadCompetency || 'Competency Card'}
                </button>

                <input
                  type="file"
                  ref={(el) => { fileInputRefs.current[`award_${index}`] = el; }}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => handleDocumentUpload(index, 'award', e)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[`award_${index}`]?.click()}
                  className="flex items-center px-3 py-2 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition"
                >
                  <Star className="w-4 h-4 mr-1" />
                  {t.step2.uploadAward || 'Awards / Honors'}
                </button>
              </div>

              {/* Uploaded Documents List */}
              {secteur.documents.length > 0 && (
                <div className="mt-3 space-y-2">
                  {secteur.documents.map((doc, docIndex) => (
                    <div key={docIndex} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded mr-2 ${
                          doc.type === 'cv' ? 'bg-blue-100 text-blue-700' :
                          doc.type === 'competency' ? 'bg-green-100 text-green-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {getDocTypeLabel(doc.type)}
                        </span>
                        <span className="text-sm text-gray-700 truncate max-w-[200px]">{doc.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(index, docIndex)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              {/* Canada */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <label className="block text-sm font-semibold text-[#1D314D]">
                  {lang === 'fr' ? 'Canada (provinces)' : 'Canada (provinces)'}
                </label>
                <Select value="" onValueChange={(v) => {
                  if (v && !secteur.juridictionsCanada.includes(v)) {
                    const ns = [...secteurs];
                    ns[index].juridictionsCanada = [...ns[index].juridictionsCanada, v];
                    setSecteurs(ns);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={lang === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {CANADIAN_PROVINCES[lang].map((province, idx) => (
                      <SelectItem key={idx} value={CANADIAN_PROVINCES.en[idx]}>{province}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secteur.juridictionsCanada.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {secteur.juridictionsCanada.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {CANADIAN_PROVINCES[lang][CANADIAN_PROVINCES.en.indexOf(item)] || item}
                        <button type="button" onClick={() => {
                          const ns = [...secteurs];
                          ns[index].juridictionsCanada = ns[index].juridictionsCanada.filter(i => i !== item);
                          setSecteurs(ns);
                        }} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* USA */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <label className="block text-sm font-semibold text-[#1D314D]">
                  {lang === 'fr' ? 'États-Unis (États)' : 'USA (States)'}
                </label>
                <Select value="" onValueChange={(v) => {
                  if (v && !secteur.juridictionsUSA.includes(v)) {
                    const ns = [...secteurs];
                    ns[index].juridictionsUSA = [...ns[index].juridictionsUSA, v];
                    setSecteurs(ns);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={lang === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secteur.juridictionsUSA.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {secteur.juridictionsUSA.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {item}
                        <button type="button" onClick={() => {
                          const ns = [...secteurs];
                          ns[index].juridictionsUSA = ns[index].juridictionsUSA.filter(i => i !== item);
                          setSecteurs(ns);
                        }} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Europe */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <label className="block text-sm font-semibold text-[#1D314D]">
                  {lang === 'fr' ? 'Europe (pays)' : 'Europe (countries)'}
                </label>
                <Select value="" onValueChange={(v) => {
                  if (v && !secteur.juridictionsEurope.includes(v)) {
                    const ns = [...secteurs];
                    ns[index].juridictionsEurope = [...ns[index].juridictionsEurope, v];
                    setSecteurs(ns);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={lang === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {EUROPEAN_COUNTRIES[lang].map((country, idx) => (
                      <SelectItem key={idx} value={EUROPEAN_COUNTRIES.en[idx]}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secteur.juridictionsEurope.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {secteur.juridictionsEurope.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {EUROPEAN_COUNTRIES[lang][EUROPEAN_COUNTRIES.en.indexOf(item)] || item}
                        <button type="button" onClick={() => {
                          const ns = [...secteurs];
                          ns[index].juridictionsEurope = ns[index].juridictionsEurope.filter(i => i !== item);
                          setSecteurs(ns);
                        }} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Other Countries */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <label className="block text-sm font-semibold text-[#1D314D]">
                  {lang === 'fr' ? 'Autres pays' : 'Other countries'}
                </label>
                <Select value="" onValueChange={(v) => {
                  if (v && !secteur.juridictionsOther.includes(v)) {
                    const ns = [...secteurs];
                    ns[index].juridictionsOther = [...ns[index].juridictionsOther, v];
                    setSecteurs(ns);
                  }
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder={lang === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {OTHER_COUNTRIES[lang].map((country, idx) => (
                      <SelectItem key={idx} value={OTHER_COUNTRIES.en[idx]}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secteur.juridictionsOther.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {secteur.juridictionsOther.map((item, idx) => (
                      <span key={idx} className="bg-[#1D314D]/10 text-[#1D314D] px-2 py-1 rounded text-xs flex items-center gap-1">
                        {OTHER_COUNTRIES[lang][OTHER_COUNTRIES.en.indexOf(item)] || item}
                        <button type="button" onClick={() => {
                          const ns = [...secteurs];
                          ns[index].juridictionsOther = ns[index].juridictionsOther.filter(i => i !== item);
                          setSecteurs(ns);
                        }} className="text-[#1D314D] hover:text-[#CEAB65]">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        ))}

        {secteurs.length < 5 && (
          <button
            type="button"
            onClick={addSecteur}
            className="w-full p-4 border-2 border-dashed border-purple-300 rounded-xl text-purple-600 hover:bg-purple-50 hover:border-purple-500 transition-all duration-300 flex items-center justify-center font-semibold"
          >
            <Plus className="mr-2 h-5 w-5" />
            {t.step2.addSector} ({secteurs.length}/5)
          </button>
        )}

        {Object.keys(errors).length > 0 && (
          <div className="p-4 bg-red-100 border-2 border-red-400 text-red-800 rounded-lg text-center font-semibold animate-pulse">
            ⚠️ {t.step2.correctErrors}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="button"
            onClick={() => prevStep()}
            className="px-6 py-3 border-2 border-slate-300 rounded-lg text-slate-700 font-bold hover:bg-slate-50 transition-all duration-300 flex items-center justify-center"
          >
            <ArrowRight className="mr-2 h-5 w-5 rotate-180" /> {t.step2.back}
          </button>
          <button
            type="submit"
            className="flex-1 rounded-lg bg-[#CEAB65] hover:bg-[#b8954f] px-8 py-4 text-[#1D314D] text-lg font-bold transition-all duration-300 shadow-lg flex items-center justify-center"
          >
            {t.step2.continueButton}
            <ArrowRight className="ml-3 h-6 w-6" />
          </button>
        </div>
      </div>
    </form>
  );
};

// --- ÉTAPE 3: Profil et Description ---
const ProfileDescriptionForm = ({ 
  initialData, 
  nextStep, 
  prevStep,
  t
}: { 
  initialData: BuyerData; 
  nextStep: (data: Partial<BuyerData>) => void;
  prevStep: () => void;
  t: TranslationType;
}) => {
  const [profilePhoto, setProfilePhoto] = useState<File | null>(initialData.profilePhoto);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const [selfDescription, setSelfDescription] = useState(initialData.selfDescription || '');
  const [experience, setExperience] = useState(initialData.experience || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    nextStep({
      profilePhoto,
      selfDescription,
      experience
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-8">
      <div className="space-y-6">
        <div className="bg-[#CEAB65]/10 border-l-4 border-[#CEAB65] p-4 rounded">
          <p className="text-sm text-[#1D314D] font-semibold">
            🎯 {t.step3Profile?.hint || 'Complete your profile to stand out to sellers'}
          </p>
        </div>

        {/* Photo de Profil */}
        <div className="space-y-4 p-6 bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-blue-200 rounded-xl">
          <h3 className="font-bold text-lg text-gray-800 flex items-center">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
              <Camera className="w-4 h-4" />
            </div>
            {t.step3Profile?.photoTitle || 'Profile Photo'}
          </h3>
          
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              {profilePhotoPreview ? (
                <div className="relative">
                  <img 
                    src={profilePhotoPreview} 
                    alt="Profile preview" 
                    className="w-32 h-32 rounded-full object-cover border-4 border-blue-300 shadow-lg"
                  />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 border-4 border-dashed border-gray-300 flex items-center justify-center">
                  <User className="w-12 h-12 text-gray-400" />
                </div>
              )}
            </div>
            
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handlePhotoUpload}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t.step3Profile?.uploadPhoto || 'Upload Photo'}
            </button>
            <p className="text-xs text-gray-500">
              {t.step3Profile?.photoHint || 'JPG, PNG. Max 5MB. A professional photo helps build trust.'}
            </p>
          </div>
        </div>

        {/* Self Description */}
        <div className="space-y-4 p-6 bg-gradient-to-br from-gray-50 to-green-50 border-2 border-green-200 rounded-xl">
          <h3 className="font-bold text-lg text-gray-800 flex items-center">
            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
              <User className="w-4 h-4" />
            </div>
            {t.step3Profile?.descriptionTitle || 'About You'}
          </h3>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.step3Profile?.descriptionLabel || 'Describe yourself and what makes you an ideal buyer'}
            </label>
            <textarea
              className="w-full rounded-lg border-2 border-gray-300 p-4 focus:ring-2 focus:ring-green-500 transition min-h-[150px] resize-y"
              placeholder={t.step3Profile?.descriptionPlaceholder || 'Tell sellers about your background, motivations, and what you bring to the table...'}
              value={selfDescription}
              onChange={(e) => setSelfDescription(e.target.value)}
              maxLength={2000}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">
              {selfDescription.length}/2000
            </p>
          </div>
        </div>

        {/* Experience */}
        <div className="space-y-4 p-6 bg-gradient-to-br from-gray-50 to-purple-50 border-2 border-purple-200 rounded-xl">
          <h3 className="font-bold text-lg text-gray-800 flex items-center">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
              <Briefcase className="w-4 h-4" />
            </div>
            {t.step3Profile?.experienceTitle || 'Your Experience'}
          </h3>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.step3Profile?.experienceLabel || 'Describe your relevant experience and expertise'}
            </label>
            <textarea
              className="w-full rounded-lg border-2 border-gray-300 p-4 focus:ring-2 focus:ring-purple-500 transition min-h-[150px] resize-y"
              placeholder={t.step3Profile?.experiencePlaceholder || 'Share your professional experience, past acquisitions, industry knowledge, management skills...'}
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              maxLength={2000}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">
              {experience.length}/2000
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="button"
            onClick={() => prevStep()}
            className="px-6 py-3 border-2 border-slate-300 rounded-lg text-slate-700 font-bold hover:bg-slate-50 transition-all duration-300 flex items-center justify-center"
          >
            <ArrowRight className="mr-2 h-5 w-5 rotate-180" /> {t.step2.back}
          </button>
          <button
            type="submit"
            className="flex-1 rounded-lg bg-[#CEAB65] hover:bg-[#b8954f] px-8 py-4 text-[#1D314D] text-lg font-bold transition-all duration-300 shadow-lg flex items-center justify-center"
          >
            {t.step3Profile?.continueButton || 'Continue to Terms'}
            <ArrowRight className="ml-3 h-6 w-6" />
          </button>
        </div>
      </div>
    </form>
  );
};

// --- ÉTAPE 4: Termes et Conditions ---
const TermsAndConditions = ({ 
  nextStep, 
  prevStep, 
  formData,
  t
}: { 
  nextStep: (data: BuyerData) => void;
  prevStep: () => void;
  formData: BuyerData;
  t: TranslationType;
}) => {
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const { language } = useLanguage();

  const handleSubmit = () => {
    if (accepted) {
      setError('');
      nextStep(formData);
    } else {
      setError(t.step4Terms?.acceptError || t.step3.acceptError);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="space-y-6">
        {/* Link to Full Terms */}
        <div className="bg-slate-100 border border-slate-300 rounded-lg p-6">
          <label className="block text-sm font-medium text-[#1D314D] mb-3">
            {language === 'fr' ? "Termes et Conditions" : "Terms and Conditions"} <span className="text-red-500">*</span>
          </label>
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-3 bg-[#CEAB65] text-[#1D314D] rounded-lg hover:bg-[#D4B87A] transition-colors font-semibold"
          >
            {language === 'fr' ? "Voir les Termes et Conditions complets" : "View Full Terms & Conditions"}
            <ExternalLink size={18} />
          </Link>
          <p className="text-xs text-slate-500 mt-2">
            {language === 'fr' 
              ? "Inclut l'Annexe A spécifique aux Acheteurs"
              : "Includes Annex A specific to Buyers"}
          </p>
        </div>

        <div className="bg-[#2a3f5f] p-5 rounded-xl">
          <label className="flex items-start space-x-4 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => {
                setAccepted(e.target.checked);
                if (e.target.checked) setError('');
              }}
              className="form-checkbox h-6 w-6 text-[#CEAB65] rounded mt-1 cursor-pointer flex-shrink-0"
            />
            <span className="text-white font-semibold leading-relaxed text-sm">
              {language === 'fr'
                ? 'Je confirme avoir lu, compris et accepté l\'intégralité des Termes et Conditions d\'Utilisation (articles 1 à 8), incluant toutes les Annexes spécifiques aux rôles (Annexes A, B, C, D et E). Je comprends que cette acceptation constitue une signature électronique valide qui m\'engage juridiquement. *'
                : 'I confirm that I have read, understood and accepted all Terms and Conditions of Use (articles 1 to 8), including all role-specific Annexes (Annexes A, B, C, D and E). I understand that this acceptance constitutes a valid electronic signature that legally binds me. *'}
            </span>
          </label>
        </div>

        {error && (
          <div className="p-4 bg-red-100 border-2 border-red-400 text-red-800 rounded-lg text-center font-semibold animate-pulse">
            ⚠️ {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="button"
            onClick={() => prevStep()}
            className="px-6 py-3 border-2 border-slate-300 rounded-lg text-slate-700 font-bold hover:bg-slate-50 transition-all duration-300 flex items-center justify-center"
          >
            <ArrowRight className="mr-2 h-5 w-5 rotate-180" /> {t.step3.back}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 rounded-lg bg-[#CEAB65] hover:bg-[#b8954f] px-6 py-3 text-[#1D314D] text-lg font-bold transition-all duration-300 shadow-lg flex items-center justify-center"
          >
            {t.step3.continueButton}
            <CreditCard className="ml-3 h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

// --- ÉTAPE 5: Paiement ---
const PaymentPage = ({ 
  formData, 
  prevStep,
  t
}: { 
  formData: BuyerData;
  prevStep: () => void;
  t: TranslationType;
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  // Visitor plan features
  const visitorFeatures = language === 'fr' ? [
    "Parcourir les annonces publiques",
    "Voir les résumés d'entreprises",
    "Accès aux répertoires consultants & experts",
    "Créer une liste de favoris",
    "Accès à la plateforme E-Learning",
  ] : [
    "Browse public listings",
    "View business summaries",
    "Access consultant & expert directories",
    "Create a favorites list",
    "Access to E-Learning platform",
  ];

  const essentialFeatures = language === 'fr' ? [
    "Accès complet au Tableau de bord Acheteur",
    "Accès complet au Deal Monitor",
    "Recherche avancée d'entreprises",
    "Signature de NDA électronique",
    "Accès aux offres des prêteurs (anonyme avant LOI)",
    "Contact direct avec les vendeurs",
    "Messagerie sécurisée",
    "Documents confidentiels inclus",
    "BYOD à 300$ par deal soumis",
  ] : [
    "Full access to Buyer Dashboard",
    "Full access to Deal Monitor",
    "Advanced business search",
    "Electronic NDA signing",
    "Access to lender offers (anonymous until LOI)",
    "Direct contact with sellers",
    "Secure messaging",
    "Confidential documents included",
    "BYOD at $300 per deal submitted",
  ];

  const essentialPlusFeatures = language === 'fr' ? [
    "Tous les avantages Essential",
    "BYOD à 300$ par deal soumis",
    "Rapport d'analyse de marché INCLUS",
    "Support prioritaire dédié",
    "Accès anticipé aux nouvelles annonces",
    "Badge Acheteur Élite sur profil",
    "Boîte de réception sécurisée dédiée",
    "Stockage sécurisé des documents (Data Room)",
    "Outil de vidéoconférence",
    "Accès à la plateforme E-Learning",
  ] : [
    "All Essential benefits",
    "BYOD at $300 per deal submitted",
    "Market Analysis Report INCLUDED",
    "Dedicated priority support",
    "Early access to new listings",
    "Elite Buyer badge on profile",
    "Secure dedicated deal MailBox",
    "Secure document storage (Data Room)",
    "Video conferencing tool",
    "Access to E-Learning platform",
  ];

  const acquisitionProFeatures = language === 'fr' ? [
    "Tous les avantages Essential Plus",
    "Rapports Deal Quality illimités",
    "Rapports Financing Feasibility illimités",
    "3 Market Studies inclus/an",
    "2 Plans Stratégiques inclus/an",
    "2 Market Comparison inclus/an",
    "Badge Acheteur Sérieux Vérifié sur le profil",
    "Priorité maximale sur les demandes de contact",
    "BYOD : 199$/deal soumis",
    "Accès E-Learning complet",
    "Frais de gestion réduits : 0,75% au lieu de 1%",
  ] : [
    "All Essential Plus benefits",
    "Unlimited Deal Quality Reports",
    "Unlimited Financing Feasibility Reports",
    "3 Market Studies included/year",
    "2 Strategic Plans included/year",
    "2 Market Comparison Reports included/year",
    "Badge Verified Serious Buyer on profile",
    "Maximum priority on contact requests",
    "BYOD: $199/deal submitted",
    "Full E-Learning access",
    "Reduced management fee: 0.75% instead of 1%",
  ];

  const byodFeatures = language === 'fr' ? [
    "Accès complet à l'Espace Acheteur",
    "Soumission pour analyse et préfinancement",
    "Offres des prêteurs sur votre tableau de bord",
    "MailBox sécurisée dédiée au deal",
    "Dépôt de document sécurisé (Data Room)",
    "Outil de visioconférence",
    "Accès à la plateforme E-Learning",
  ] : [
    "Full access to Buyer Dashboard",
    "Submission for analysis and pre-financing",
    "Lender offers on your dashboard",
    "Secure dedicated deal MailBox",
    "Secure document storage (Data Room)",
    "Video conferencing tool",
    "Access to E-Learning platform",
  ];

  const essentialSavings      = Math.round(((TIERS.essential.regularPrice - TIERS.essential.price) / TIERS.essential.regularPrice) * 100);
  const essentialPlusSavings  = Math.round(((TIERS.essentialPlus.regularPrice - TIERS.essentialPlus.price) / TIERS.essentialPlus.regularPrice) * 100);
  const acquisitionProSavings = Math.round(((TIERS.acquisitionPro.regularPrice - TIERS.acquisitionPro.price) / TIERS.acquisitionPro.regularPrice) * 100);
  const byodSavings           = Math.round(((TIERS.byod.regularPrice - TIERS.byod.price) / TIERS.byod.regularPrice) * 100);

  // Helper: create Firebase account or add buyer role to the current account
  const registerBuyerLocally = async (tier: string) => {
    const registrationPayload = {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      displayName: `${formData.firstName} ${formData.lastName}`,
      phone: formData.telephone,
      address: {
        street: formData.adresseRue,
        city: formData.ville,
        stateProvince: formData.province,
        zipPostalCode: formData.codePostal,
        country: formData.pays,
      },
      howHeard: formData.sourcesMarketing,
      selfDescription: formData.selfDescription,
      experience: formData.experience,
      sectors: formData.secteurs.map(s => ({
        sector: s.secteur,
        yearsOfExperience: s.anneesExperience,
        licensePermit: s.licencePermis,
        jurisdictionsCanada: s.juridictionsCanada,
        jurisdictionsUSA: s.juridictionsUSA,
      })),
      subscriptionTier: tier,
      language,
    };

    let user = auth.currentUser;

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.motDePasse);
      user = userCredential.user;

      // Force token refresh so backend sees latest auth state
      await user.getIdToken(true);

      // CRITICAL: must complete before checkout — backend reads users/{uid}.email
      await setDoc(doc(db, 'users', user.uid), {
        ...registrationPayload,
        email: formData.email,
        roles: ['buyer'],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await assignRoleViaNestJS(user, 'buyer');
      // Email Firebase de vérification supprimé : l'email de bienvenue est envoyé après paiement
      // via le webhook Stripe checkout.session.completed -> sendWelcomeEmail.
    } catch (error) {
      if (!isEmailAlreadyInUseError(error)) throw error;
      user = await addRoleToCurrentUser({
        email: formData.email,
        password: formData.motDePasse,
        role: 'buyer',
        profileUpdates: {
          ...registrationPayload,
          updatedAt: serverTimestamp(),
        },
      });
    }

    if (!user) throw new Error('Registration user unavailable');

    // Sauvegarder les données d'inscription
    await saveRegistrationData(user.uid, 'buyer', registrationPayload);
  };

  const handleVisitorRegistration = async () => {
    setIsLoading('visitor');
    try {
      await registerBuyerLocally('visitor');

      setSelectedTier('visitor');
      setRegistrationComplete(true);
      
      setTimeout(() => {
        navigate('/buyer/dashboard');
      }, 8000);
    } catch (error: unknown) {
      console.error('Visitor registration error:', error);
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: getErrorMessage(error, 'Registration error'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleSubscription = async (tier: 'essential' | 'essentialPlus' | 'acquisitionPro') => {
    setIsLoading(tier);
    try {
      await registerBuyerLocally(tier);

      // Force Firebase token refresh before checkout
      try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (currentUser) {
          await currentUser.getIdToken(true); // force refresh = true
        }
      } catch (tokenErr) {
        console.warn('Token refresh failed:', tokenErr);
      }

     const data = await startSubscriptionCheckout({
          planId: tier,
          userId: auth.currentUser?.uid || '',
          lang: language as 'fr' | 'en',
          termsVersion: '1.0',
        });
      if (!data?.url) throw new Error('No checkout URL returned');

      window.open(data.url, '_blank');
      
      setSelectedTier(tier);
      setRegistrationComplete(true);
    } catch (error: unknown) {
      console.error('Checkout error:', error);
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: getErrorMessage(error, 'Payment error'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleBYODPayment = async () => {
    setIsLoading('byod');
    try {
      await registerBuyerLocally('byod');

const data = await startOneTimeCheckout({
  planId: 'byod',
  userId: auth.currentUser?.uid || '',
  lang: language as 'fr' | 'en',
  termsVersion: '1.0',
});
if (!data?.url) throw new Error('No checkout URL returned');
window.open(data.url, '_blank');
    } catch (error: unknown) {
      console.error('BYOD checkout error:', error);
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: getErrorMessage(error, 'Payment error'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(null);
    }
  };

  // Success Screen after registration
  if (registrationComplete) {
    return (
      <div className="p-6 md:p-10">
        <div className="max-w-2xl mx-auto bg-white p-10 rounded-2xl shadow-2xl border-t-8 border-green-500 text-center">
          <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-16 h-16 text-green-600 animate-bounce" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'fr' ? 'Inscription Complétée!' : 'Registration Complete!'}
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            {language === 'fr' 
              ? `Bienvenue ${formData.firstName}! Votre compte Acheteur a été créé avec succès.` 
              : `Welcome ${formData.firstName}! Your Buyer account has been created successfully.`}
          </p>
          
          {selectedTier === 'visitor' ? (
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200 mb-6">
              <h3 className="font-bold text-blue-900 mb-2">
                {language === 'fr' ? 'Forfait Visiteur activé' : 'Visitor Plan activated'}
              </h3>
              <p className="text-blue-700 text-sm">
                {language === 'fr' 
                  ? 'Vous avez un accès limité au Deal Monitor. Mettez à niveau vers Essential ou Essential Plus à tout moment pour débloquer toutes les fonctionnalités.'
                  : 'You have limited access to the Deal Monitor. Upgrade to Essential or Essential Plus anytime to unlock all features.'}
              </p>
            </div>
          ) : (
            <div className="bg-green-50 p-6 rounded-xl border border-green-200 mb-6">
              <h3 className="font-bold text-green-900 mb-2">
                {language === 'fr' ? 'Fenêtre de paiement ouverte' : 'Payment window opened'}
              </h3>
              <p className="text-green-700 text-sm">
                {language === 'fr' 
                  ? 'Une nouvelle fenêtre Stripe s\'est ouverte pour compléter votre paiement. Si elle ne s\'affiche pas, vérifiez votre bloqueur de popups.'
                  : 'A new Stripe window has opened to complete your payment. If it doesn\'t appear, check your popup blocker.'}
              </p>
            </div>
          )}
          
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/buyer/dashboard')}
              className="w-full px-8 py-4 bg-gradient-to-r from-[#1D314D] to-[#2E4663] hover:from-[#2E4663] hover:to-[#1D314D] text-white text-lg font-bold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
            >
              {language === 'fr' ? 'Accéder à mon Tableau de Bord' : 'Access my Dashboard'}
              <ArrowRight className="ml-3 h-6 w-6" />
            </button>
            
            <p className="text-sm text-gray-500 italic">
              {language === 'fr' 
                ? 'Redirection automatique dans quelques secondes...'
                : 'Automatic redirection in a few seconds...'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-[#1D314D] mb-2">
            {language === 'fr' ? 'Choisissez votre forfait' : 'Choose your plan'}
          </h2>
          <p className="text-gray-600 text-sm">
            {language === 'fr' 
              ? "Sélectionnez le plan qui correspond le mieux à vos besoins d'acquisition."
              : "Select the plan that best fits your acquisition needs."
            }
          </p>
        </div>

        {/* 4 Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          
          {/* Visitor Plan (Free) */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] border border-gray-300 flex flex-col">
            <div className="p-5 bg-gradient-to-br from-gray-600 to-gray-700 text-white text-center">
              <Eye className="w-8 h-8 text-white mx-auto mb-2" />
              <h3 className="text-xl font-bold">{language === 'fr' ? 'Visiteur' : 'Visitor'}</h3>
              <p className="text-white/70 text-xs">{language === 'fr' ? 'Accès de base' : 'Basic access'}</p>
            </div>
            
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-center mb-4">
                <div className="text-3xl font-extrabold text-gray-700">
                  {language === 'fr' ? 'Gratuit' : 'Free'}
                </div>
                <div className="text-sm text-gray-500">{language === 'fr' ? 'Aucun frais' : 'No charge'}</div>
              </div>

              <div className="space-y-2 mb-4 flex-1">
                {visitorFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handleVisitorRegistration}
                disabled={isLoading !== null}
                className={`w-full flex items-center justify-center px-4 py-3 text-sm font-bold rounded-lg shadow transition duration-300 mt-auto
                  ${isLoading === 'visitor' 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                  }
                `}
              >
                {isLoading === 'visitor' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'fr' ? 'Traitement...' : 'Processing...'}</>
                ) : (
                  <>{language === 'fr' ? 'Commencer gratuitement' : 'Start for free'} <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </button>
            </div>
          </div>

          {/* Essential Plan */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] border border-gray-200 flex flex-col">
            <div className="p-5 bg-gradient-to-br from-[#1D314D] to-[#2E4663] text-white text-center">
              <TrendingUp className="w-8 h-8 text-[#CEAB65] mx-auto mb-2" />
              <h3 className="text-xl font-bold">{TIERS.essential.name[language as 'en' | 'fr']}</h3>
              <p className="text-white/70 text-xs">{language === 'fr' ? 'Abonnement annuel' : 'Annual subscription'}</p>
            </div>
            
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-400 line-through">${TIERS.essential.regularPrice.toFixed(2)}</span>
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-{essentialSavings}%</span>
                </div>
                <div className="text-3xl font-extrabold text-[#1D314D]">${TIERS.essential.price.toFixed(2)}</div>
                <div className="text-sm text-gray-500">/ {language === 'fr' ? 'an' : 'year'}</div>
                <div className="text-xs text-[#CEAB65] font-semibold mt-1">🚀 {language === 'fr' ? 'Prix de lancement' : 'Launch price'}</div>
              </div>

              <div className="space-y-2 mb-4 flex-1">
                {essentialFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-3.5 h-3.5 text-[#CEAB65] mt-0.5 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleSubscription('essential')}
                disabled={isLoading !== null}
                className={`w-full flex items-center justify-center px-4 py-3 text-sm font-bold rounded-lg shadow transition duration-300 mt-auto
                  ${isLoading === 'essential' 
                    ? 'bg-[#CEAB65]/60 text-[#1D314D] cursor-not-allowed' 
                    : 'bg-[#CEAB65] text-[#1D314D] hover:bg-[#b8954f]'
                  }
                `}
              >
                {isLoading === 'essential' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'fr' ? 'Traitement...' : 'Processing...'}</>
                ) : (
                  <>{language === 'fr' ? 'Souscrire' : 'Subscribe'} <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </button>
            </div>
          </div>

          {/* Essential Plus Plan */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] border-2 border-[#CEAB65] relative flex flex-col">
            <div className="absolute top-0 right-0 bg-[#CEAB65] text-[#1D314D] px-3 py-1 text-xs font-bold rounded-bl-lg">
              {language === 'fr' ? 'Populaire' : 'Popular'}
            </div>

            <div className="p-5 bg-gradient-to-br from-[#1D314D] to-[#2E4663] text-white text-center">
              <Star className="w-8 h-8 text-[#CEAB65] mx-auto mb-2" />
              <h3 className="text-xl font-bold">{TIERS.essentialPlus.name[language as 'en' | 'fr']}</h3>
              <p className="text-white/70 text-xs">{language === 'fr' ? 'Abonnement annuel' : 'Annual subscription'}</p>
            </div>
            
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-400 line-through">${TIERS.essentialPlus.regularPrice.toFixed(2)}</span>
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-{essentialPlusSavings}%</span>
                </div>
                <div className="text-3xl font-extrabold text-[#CEAB65]">${TIERS.essentialPlus.price.toFixed(2)}</div>
                <div className="text-sm text-gray-500">/ {language === 'fr' ? 'an' : 'year'}</div>
                <div className="text-xs text-[#CEAB65] font-semibold mt-1">🚀 {language === 'fr' ? 'Prix de lancement' : 'Launch price'}</div>
              </div>

              <div className="space-y-2 mb-4 flex-1">
                {essentialPlusFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-3.5 h-3.5 text-[#CEAB65] mt-0.5 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleSubscription('essentialPlus')}
                disabled={isLoading !== null}
                className={`w-full flex items-center justify-center px-4 py-3 text-sm font-bold rounded-lg shadow transition duration-300 mt-auto
                  ${isLoading === 'essentialPlus' 
                    ? 'bg-[#CEAB65]/60 text-[#1D314D] cursor-not-allowed' 
                    : 'bg-[#CEAB65] text-[#1D314D] hover:bg-[#b8954f]'
                  }
                `}
              >
                {isLoading === 'essentialPlus' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'fr' ? 'Traitement...' : 'Processing...'}</>
                ) : (
                  <>{language === 'fr' ? 'Souscrire' : 'Subscribe'} <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </button>
            </div>
          </div>

          {/* Acquisition Pro Plan */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] border-2 border-[#CEAB65] relative flex flex-col">
            <div className="absolute top-0 right-0 bg-[#CEAB65] text-[#1D314D] px-3 py-1 text-xs font-bold rounded-bl-lg">
              {language === 'fr' ? 'Meilleure valeur' : 'Best Value'}
            </div>

            <div className="p-5 bg-gradient-to-br from-[#1D314D] to-[#2E4663] text-white text-center">
              <Star className="w-8 h-8 text-[#CEAB65] mx-auto mb-2" />
              <h3 className="text-xl font-bold">{TIERS.acquisitionPro.name[language as 'en' | 'fr']}</h3>
              <p className="text-white/70 text-xs">{language === 'fr' ? 'Abonnement annuel' : 'Annual subscription'}</p>
            </div>

            <div className="p-4 flex-1 flex flex-col">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-400 line-through">${TIERS.acquisitionPro.regularPrice.toLocaleString()}</span>
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-{acquisitionProSavings}%</span>
                </div>
                <div className="text-3xl font-extrabold text-[#CEAB65]">${TIERS.acquisitionPro.price.toLocaleString()}</div>
                <div className="text-sm text-gray-500">/ {language === 'fr' ? 'an' : 'year'}</div>
                <div className="text-xs text-[#CEAB65] font-semibold mt-1">🚀 {language === 'fr' ? 'Prix de lancement' : 'Launch price'}</div>
              </div>

              <div className="space-y-2 mb-4 flex-1">
                {acquisitionProFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-3.5 h-3.5 text-[#CEAB65] mt-0.5 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleSubscription('acquisitionPro')}
                disabled={isLoading !== null}
                className={`w-full flex items-center justify-center px-4 py-3 text-sm font-bold rounded-lg shadow transition duration-300 mt-auto
                  ${isLoading === 'acquisitionPro'
                    ? 'bg-[#CEAB65]/60 text-[#1D314D] cursor-not-allowed'
                    : 'bg-[#CEAB65] text-[#1D314D] hover:bg-[#b8954f]'
                  }
                `}
              >
                {isLoading === 'acquisitionPro' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'fr' ? 'Traitement...' : 'Processing...'}</>
                ) : (
                  <>{language === 'fr' ? 'Souscrire' : 'Subscribe'} <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </button>
            </div>
          </div>

          {/* BYOD Plan */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] border border-orange-300 relative flex flex-col">
            <div className="absolute top-0 right-0 bg-orange-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
              BYOD
            </div>

            <div className="p-5 bg-gradient-to-br from-[#1D314D] to-[#2E4663] text-white text-center">
              <Target className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <h3 className="text-xl font-bold">Bring Your Own Deal</h3>
              <p className="text-white/70 text-xs">{language === 'fr' ? 'Paiement par deal' : 'Pay per deal'}</p>
            </div>
            
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-400 line-through">${TIERS.byod.regularPrice}</span>
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-{byodSavings}%</span>
                </div>
                <div className="text-3xl font-extrabold text-orange-500">${TIERS.byod.price}</div>
                <div className="text-sm text-gray-500">/ deal</div>
                <div className="text-xs text-orange-500 font-semibold mt-1">🚀 {language === 'fr' ? 'Prix de lancement' : 'Launch price'}</div>
              </div>

              <div className="space-y-2 mb-4 flex-1">
                {byodFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-3.5 h-3.5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handleBYODPayment}
                disabled={isLoading !== null}
                className={`w-full flex items-center justify-center px-4 py-3 text-sm font-bold rounded-lg shadow transition duration-300 mt-auto
                  ${isLoading === 'byod' 
                    ? 'bg-orange-500/60 text-white cursor-not-allowed' 
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                  }
                `}
              >
                {isLoading === 'byod' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'fr' ? 'Traitement...' : 'Processing...'}</>
                ) : (
                  <>{language === 'fr' ? 'Soumettre un Deal' : 'Submit a Deal'} <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="text-center text-gray-500 text-sm flex items-center justify-center">
          <CreditCard className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Paiement sécurisé via Stripe.' : 'Secure payment via Stripe.'}
        </div>
        
        <button
          type="button"
          onClick={() => prevStep()}
          className="w-full px-6 py-3 text-slate-600 font-bold hover:text-[#1D314D] transition duration-300 flex items-center justify-center"
        >
          <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
          {t.step4.backToTerms}
        </button>
      </div>
    </div>
  );
};

// --- COMPOSANT PRINCIPAL ---
export default function RegisterBuyer() {
  const { language, setLanguage } = useLanguage();
  const t = BUYER_TRANSLATIONS[language as keyof typeof BUYER_TRANSLATIONS] || BUYER_TRANSLATIONS.en;
  
  const [step, setStep] = useState(1);
  const [buyerData, setBuyerData] = useState<BuyerData>({
    firstName: '',
    lastName: '',
    email: '',
    emailConfirmation: '',
    motDePasse: '',
    confirmMotDePasse: '',
    telephone: '',
    adresseRue: '',
    ville: '',
    province: '',
    codePostal: '',
    pays: '',
    sourcesMarketing: [],
    secteurs: [],
    profilePhoto: null,
    selfDescription: '',
    experience: ''
  });

  const nextStep = (data: Partial<BuyerData>) => {
    setBuyerData(prev => ({ ...prev, ...data }));
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stepLabels = [
    { num: 1, label: t.steps.profile },
    { num: 2, label: t.steps.sectors },
    { num: 3, label: t.steps.profileDesc || 'Profile' },
    { num: 4, label: t.steps.terms },
    { num: 5, label: t.steps.payment }
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return <PersonalInfoForm initialData={buyerData} nextStep={nextStep} t={t} />;
      case 2:
        return <SectorExpertiseForm initialData={buyerData} nextStep={nextStep} prevStep={prevStep} t={t} />;
      case 3:
        return <ProfileDescriptionForm initialData={buyerData} nextStep={nextStep} prevStep={prevStep} t={t} />;
      case 4:
        return <TermsAndConditions nextStep={nextStep} prevStep={prevStep} formData={buyerData} t={t} />;
      case 5:
        return <PaymentPage formData={buyerData} prevStep={prevStep} t={t} />;
      default:
        return <div>Navigation error.</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4 font-sans">
      {/* Top bar with Logo and Language Toggle */}
      <div className="flex justify-center items-center mb-6 relative max-w-4xl mx-auto">
        <a href="/">
          <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" />
        </a>
        <button
          onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
          className="absolute right-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 border border-gray-200 shadow-sm hover:shadow-md transition-all text-sm font-semibold text-[#1D314D]"
          title={language === 'en' ? 'Switch to French' : 'Passer en anglais'}
        >
          <Globe className="w-4 h-4 text-[#CEAB65]" />
          {language === 'en' ? 'FR' : 'EN'}
        </button>
      </div>

      <div className={`${step === 5 ? 'max-w-6xl' : 'max-w-4xl'} mx-auto`}>
        {/* Header Card - Navy */}
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-6 shadow-lg">
          <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
            {t.pageTitle}
          </h1>
          <p className="text-white/70 text-center mt-2">
            {t.step} {step} {t.of} 5
          </p>

          {/* Step Indicator */}
          <div className="mt-6">
            <div className="flex justify-between items-center">
              {stepLabels.map((s, idx, arr) => (
                <React.Fragment key={s.num}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300 ${
                      step >= s.num 
                        ? 'bg-[#CEAB65] text-[#1D314D] border-[#CEAB65]' 
                        : 'bg-transparent text-white/50 border-white/30'
                    }`}>
                      {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                    </div>
                    <span className={`text-xs mt-2 font-medium hidden sm:block ${
                      step >= s.num ? 'text-white' : 'text-white/50'
                    }`}>
                      {s.label}
                    </span>
                  </div>
                  {idx < arr.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded transition-all duration-300 ${
                      step > s.num ? 'bg-[#CEAB65]' : 'bg-white/20'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Form Content - White */}
        <div className="bg-white rounded-b-xl shadow-xl">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            {t.alreadyMember}{' '}
            <a href="/login" className="text-[#CEAB65] hover:text-[#b8954f] font-bold transition">
              {t.login}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
